import {
  Avatar,
  DatePicker,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from "@nextui-org/react";
import React, { useContext, useEffect, useState } from "react";
import closeIcon from "../../../Assets/popup/cross-square.svg";
import { AuthContext } from "Context/AuthContext";
import { Controller, useForm, useWatch } from "react-hook-form";
import addTaskIcon from "../../../Assets/frond-details/tasklist/tasklist-icon.svg";
import userIcon from "../../../Assets/frond-details/tasklist/user.svg";
import axios from "axios";
import { toast } from "react-toastify";
import { PopUpContext } from "Context/PopUpContext";
import up from "../../../Assets/create-frond/up.svg";
import calendar from "../../../Assets/create-frond/calendar.svg";
import { parseDate } from "@internationalized/date";
import TaskMediaApi from "../../../services/taskMediaApi.js";
import { Gallery } from "react-grid-gallery";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

const ViewTask = ({ isOpen, onClose, frondId, taskId }) => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(new Set());
  const [descLength, setDescLength] = useState(0);
  const [taskMedia, setTaskMedia] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);

  const {
    control,
    setValue,
    reset,
    watch,
  } = useForm({

    defaultValues: {
      name: "",
      date: null,
      title: "",
      desc: "",
      status: "",
    },
  });

  const dateValue = watch("date");
  const statusValue = watch("status");

  const { token } = useContext(AuthContext);

  const fetchTaskMedia = async (taskId) => {
    if (!taskId) return;
    
    setIsMediaLoading(true);
    try {
      const media = await TaskMediaApi.getTaskMedia(token, taskId);
      
      if (Array.isArray(media) && media.length > 0) {
        setTaskMedia(media);
      } else {
        setTaskMedia([]);
      }
    } catch (error) {
      console.error('Error fetching task media:', error);
      setTaskMedia([]);
    } finally {
      setIsMediaLoading(false);
    }
  };

  const getTaskData = async () => {
    setIsDataLoading(true);
    try {
      const { data } = await axios.get(
        `/tasks/${taskId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const task = data?.response?.data;
      setTaskData(task);

      const activeUsers = task?.assigned_to?.filter(
        (user) => user.status === true
      );

      if (activeUsers && activeUsers.length > 0) {
        setSelectedUser(new Set(activeUsers.map((user) => user.name)));
        setValue(
          "name",
          activeUsers.map((user) => user.name)
        );
      }

      if (task?.task?.due_date) {
        const dateArr = task?.task?.due_date?.split("-");
        if (dateArr && dateArr.length === 3) {
          setValue(
            "date",
            parseDate(
              `${dateArr[0]}-${dateArr[1].padStart(2, "0")}-${dateArr[2].padStart(2, "0")}`
            )
          );
        }
      }

      setValue("desc", task?.task?.description || "");
      setValue("title", task?.task?.title || "");
      
      let statusText = "";
      if (task?.task?.status === "pending") {
        statusText = "Pending";
      } else if (task?.task?.status === "in_progress") {
        statusText = "In Progress";
      } else if (task?.task?.status === "completed") {
        statusText = "Completed";
      } else if (task?.task?.status === "archived") {
        statusText = "Archived";
      }
      setValue("status", statusText);

      setDescLength(task?.task?.description?.length || 0);
      
      // Fetch task media after getting task data
      if (task?.task?.id) {
        await fetchTaskMedia(task.task.id);
      }
    } catch (error) {
      toast(error?.response?.data?.message, {
        autoClose: 1500,
        position: "bottom-right",
        hideProgressBar: true,
        className: "rounded-4xl custom-toast error-toast",
      });
    }
    setIsDataLoading(false);
  };

  const getMembers = async () => {
    try {
      const { data } = await axios.get(
        `/projects/${frondId}/membersassign`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(data);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    if (isOpen && taskId) {
      getTaskData();
      getMembers();
    }
  }, [isOpen, taskId, frondId]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        handleResize();
      }, 1000);
    }
  }, [isOpen]);

  const handleResize = () => {
    const scrollOutter = document.querySelector(".scroll-outter-view");
    const scrollInner = document.querySelector(".scroll-inner-view");

    if (scrollOutter && scrollInner) {
      const scrollOutterWidth = scrollOutter.offsetWidth;
      const scrollInnerWidth = scrollInner.offsetWidth;
      scrollOutter.style.width = `calc(100% + ${
        scrollOutterWidth - scrollInnerWidth
      }px)`;
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [descLength, taskMedia.length]);

  const statusList = ["Pending", "In Progress", "Completed", "Archived"];

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Prepare gallery images for lightbox
  const getGalleryImages = () => {
    if (!taskMedia || taskMedia.length === 0) return [];
    
    return taskMedia
      .filter(m => m.type === 'image' || m.mimeType?.startsWith('image/'))
      .map((img) => ({
        src: img.thumbnail || img.url || img.original_url,
        width: 1,
        height: 1,
        originalSrc: img.url || img.original_url,
        id: img.id,
        name: img.name || img.filename || `Image ${img.id}`,
        caption: img.caption || '',
      }));
  };

  // Get task documents
  const getTaskDocuments = () => {
    if (!taskMedia || taskMedia.length === 0) return [];
    return taskMedia.filter(m => m.type === 'document');
  };

  // Get document icon based on mime type
  const getDocumentIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📊';
    if (mimeType.includes('text') || mimeType === 'text/plain') return '📄';
    return '📄';
  };

  // Open lightbox for media
  const openLightbox = (index) => {
    const images = taskMedia
      .filter(m => m.type === 'image' || m.mimeType?.startsWith('image/'))
      .map(img => ({
        src: img.url || img.original_url,
        title: img.name || img.filename || `Image ${img.id + 1}`,
        caption: img.caption || '',
      }));

    setLightboxImages(images);
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  // Render media gallery + documents
  const renderMediaGallery = () => {
    if (!taskMedia || taskMedia.length === 0) return null;

    const images = getGalleryImages();
    const documents = getTaskDocuments();

    return (
      <div className="w-full space-y-4">
        {/* Images */}
        {images.length > 0 && (
          <div>
            <label className="text-sm text-cardGrayColor font-normal mb-2 block">
             <span>
              <img width="15" height="15" src="https://img.icons8.com/ios/20/578FCA/image--v1.png" alt="image--v1"/> 
              <span> Images ({images.length})</span>
              </span> 
              
            </label>
            <Gallery
              onClick={(index) => openLightbox(index)}
              enableImageSelection={false}
              images={images}
              containerWidth={400}
              thumbnailStyle={{
                cursor: "pointer",
                width: "80px",
                height: "80px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
              rowHeight={80}
              margin={4}
            />
          </div>
        )}
        
       {/* Documents List */}
{documents.length > 0 && (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-2">
       <span className="text-lg"><img width="15" height="15" src="https://img.icons8.com/ultraviolet/20/EB4C4C/live-folder.png" alt="live-folder"/></span>
      <span className="text-sm font-medium text-cardGrayColor">Documents ({documents.length})</span>
    </div>
    <div className="flex overflow-x-auto gap-2 pb-2">
      {documents.map((doc) => (
        <div 
          key={doc.id}
          className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer group"
          onClick={() => window.open(doc.url, '_blank')}
        >
          <span className="text-lg">
            {getDocumentIcon(doc.mimeType)}
          </span>
          <span className="text-sm font-medium text-textColor max-w-[120px] truncate">
            {doc.name || doc.filename || 'Document'}
          </span>
          <span className="text-xs text-cardGrayColor">
            {doc.sizeFormatted || `${(doc.size / 1024).toFixed(0)} KB`}
          </span>
        </div>
      ))}
    </div>
  </div>
)}
        
        {isMediaLoading && (
          <div className="text-sm text-cardGrayColor mt-2">Loading media...</div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        isDismissable={!lightboxOpen}
        onOpenChange={() => {
          onClose();
          setTimeout(() => {
            reset();
            setSelectedUser(new Set([]));
            setTaskData(null);
            setTaskMedia([]);
          }, 1000);
        }}
        placement="center"
        backdrop="opaque"
        classNames={{
          backdrop: "z-[999]",
          wrapper: "z-[9999] overflow-hidden",
          footer: "justify-center items-center",
          body: "px-[30px] mb-2.5",
          closeButton: "hidden",
          base: "rounded-[20px] p-5",
        }}
      >
        <div>
          <ModalContent>
            {(closeModal) => (
              <>
                <ModalHeader className="flex justify-between gap-2.5 items-center p-0 pb-[15px] pt-[15px] sm:px-5 px-2.5 mb-[15px] relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[0.5px] after:bg-grayBorder">
                  <h3 className="text-lg text-textColor font-bold leading-[10.35px]">
                    View Task
                  </h3>
                  <button onClick={closeModal} aria-label="Close">
                    <img src={closeIcon} alt="Close" className="w-6 h-6" />
                  </button>
                </ModalHeader>
                <div className="max-h-[60vh] overflow-y-auto scroll-outter-view">
                  <ModalBody className="w-[100%] scroll-inner-view">
                    <div className="relative about-edit-img sm:min-h-[295px] sm:max-h-[295px] min-h-[200px] max-h-[200px] sm:min-w-[100%] sm:max-w-[100%] min-w-[100%] max-w-[100%]">
                      <div className="content flex flex-col gap-5 items-center">
                        <Image
                          src={addTaskIcon}
                          className="w-[96px] h-[96px]"
                          alt="Task"
                        />

                        {/* Assigned to - Read only */}
                        <div className="w-full">
                          <label className="text-sm text-cardGrayColor font-normal mb-2 block">
                            Assigned to
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {taskData?.assigned_to && taskData.assigned_to.length > 0 ? (
                              taskData.assigned_to.map((user) => (
                                <div
                                  key={user.id}
                                  className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg"
                                >
                                  <Avatar
                                    alt={user.name}
                                    className="flex-shrink-0"
                                    size="sm"
                                    src={user.img_url}
                                  />
                                  <span className="text-sm text-textColor">
                                    {user.name}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className="text-sm text-cardGrayColor">-</span>
                            )}
                          </div>
                        </div>

                        {/* Due Date - Read only */}
                        <div className="w-full">
                          <label className="text-sm text-cardGrayColor font-normal mb-2 block">
                            Due Date
                          </label>
                          <div className="text-textColor text-base">
                            {taskData?.task?.due_date
                              ? formatDate(taskData.task.due_date)
                              : "-"}
                          </div>
                        </div>

                        {/* Title - Read only */}
                        <div className="w-full">
                          <label className="text-sm text-cardGrayColor font-normal mb-2 block">
                            Title
                          </label>
                          <div className="text-textColor text-base font-medium">
                            {taskData?.task?.title || "-"}
                          </div>
                        </div>

                        {/* Task Media Gallery */}
                        {renderMediaGallery()}

                        {/* Status - Read only */}
                        <div className="w-full">
                          <label className="text-sm text-cardGrayColor font-normal mb-2 block">
                            Status
                          </label>
                          <div className="text-textColor text-base">
                            {statusValue || "-"}
                          </div>
                        </div>

                        {/* Description - Read only */}
                        <div className="w-full mb-5">
                          <label className="text-sm text-cardGrayColor font-normal mb-2 block">
                            Description
                          </label>
                          <div className="text-textColor text-base whitespace-pre-wrap">
                            {taskData?.task?.description || "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                     
                  </ModalBody>
                </div>
                <ModalFooter className="shadow-modalShadow">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="text-base font-bold leading-[18.4px] text-white flex justify-center items-center bg-mainGradiant rounded-[27px] py-[11px] px-[25px] w-[88px] sm:min-h-[39px] sm:max-h-[39px] min-h-[30px] max-h-[30px]"
                  >
                    Close
                  </button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
          {lightboxOpen && (
                    <Lightbox
                      mainSrc={lightboxImages[photoIndex]?.src}
                      nextSrc={lightboxImages[(photoIndex + 1) % lightboxImages.length]?.src}
                      prevSrc={lightboxImages[(photoIndex + lightboxImages.length - 1) % lightboxImages.length]?.src}
                      onCloseRequest={() => setLightboxOpen(false)}
                      onMovePrevRequest={() =>
                        setPhotoIndex((photoIndex + lightboxImages.length - 1) % lightboxImages.length)
                      }
                      onMoveNextRequest={() =>
                        setPhotoIndex((photoIndex + 1) % lightboxImages.length)
                      }
                      imageTitle={lightboxImages[photoIndex]?.title}
                      imageCaption={lightboxImages[photoIndex]?.caption}
                      reactModalStyle={{ zIndex: 100001 }}
                    />
                  )}
        </div>
         {/* Lightbox for fullscreen image viewing */}
     
      </Modal>

     
    </>
  );
};

export default ViewTask;